import { useState, useEffect, useCallback } from 'react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from 'recharts';
import {
  ShoppingCart, Truck, Package, TrendingUp,
  RefreshCw, ChevronRight, ArrowUpRight, ArrowDownRight, Download,
} from 'lucide-react';
import api from '../services/api';
import { moneda, cantidad } from '../utils/format';
import { exportarVentas, exportarCompras, exportarStock, exportarRentabilidad } from '../utils/excel';
import toast from 'react-hot-toast';

// ── Fecha helpers ─────────────────────────────────────────────────────────
const ymd = (d) => d.toISOString().slice(0, 10);
const today = () => ymd(new Date());
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return ymd(x); };

const PRESETS = [
  {
    label: 'Hoy',
    get: () => { const d = today(); return { desde: d, hasta: d }; },
  },
  {
    label: 'Ayer',
    get: () => { const d = addDays(new Date(), -1); return { desde: d, hasta: d }; },
  },
  {
    label: 'Esta semana',
    get: () => {
      const now = new Date();
      const dow = now.getDay() || 7;
      const lun = new Date(now); lun.setDate(now.getDate() - dow + 1);
      return { desde: ymd(lun), hasta: today() };
    },
  },
  {
    label: 'Este mes',
    get: () => {
      const now = new Date();
      return { desde: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`, hasta: today() };
    },
  },
  {
    label: 'Mes anterior',
    get: () => {
      const now = new Date();
      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const last  = new Date(now.getFullYear(), now.getMonth(), 0);
      return { desde: ymd(first), hasta: ymd(last) };
    },
  },
];

const TABS = [
  { id: 'ventas',       label: 'Ventas',        icon: ShoppingCart },
  { id: 'compras',      label: 'Compras',        icon: Truck        },
  { id: 'stock',        label: 'Stock',          icon: Package      },
  { id: 'rentabilidad', label: 'Rentabilidad',   icon: TrendingUp   },
];

// ── KPI Card ──────────────────────────────────────────────────────────────
function KpiCard({ label, value, sub, color = 'primary' }) {
  const colorMap = {
    primary: 'bg-primary-50 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400',
    green:   'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    blue:    'bg-sky-50 dark:bg-sky-500/10 text-sky-600 dark:text-sky-400',
    amber:   'bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400',
    red:     'bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400',
  };
  return (
    <div className="card dark:bg-slate-800 flex flex-col gap-2">
      <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">{label}</p>
      <p className="text-2xl font-bold tabular-nums text-slate-900 dark:text-white leading-none">{value}</p>
      {sub && <p className={`text-xs font-medium ${colorMap[color]}`}>{sub}</p>}
    </div>
  );
}

// ── Chart tooltip ─────────────────────────────────────────────────────────
function ChartTip({ active, payload, label, isMoney = true }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-dropdown px-4 py-3 text-sm border border-slate-100 dark:border-slate-700">
      <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1">{label}</p>
      <p className="text-slate-900 dark:text-white font-bold tabular-nums">
        {isMoney ? moneda(payload[0].value) : cantidad(payload[0].value)}
      </p>
    </div>
  );
}

// ── Barra de progreso para top items ──────────────────────────────────────
function TopList({ items, labelKey, valueKey, isMoney = true, limit = 8 }) {
  const maxVal = Math.max(...items.map(i => i[valueKey]), 1);
  const COLORS = ['#7175ef','#8b8ff4','#a5a8f7','#bfc1fa','#d4d5fc','#e4e5fd','#f0f1fe','#f6f7ff'];
  return (
    <div className="space-y-3">
      {items.slice(0, limit).map((item, i) => (
        <div key={i}>
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-[10px] font-bold text-slate-300 dark:text-slate-600 w-4 shrink-0">{i + 1}</span>
              <span className="text-[13px] font-medium text-slate-700 dark:text-slate-300 truncate">{item[labelKey]}</span>
            </div>
            <span className="text-[12px] font-semibold text-slate-500 dark:text-slate-400 tabular-nums shrink-0 ml-3">
              {isMoney ? moneda(item[valueKey]) : cantidad(item[valueKey])}
            </span>
          </div>
          <div className="h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{ width: `${(item[valueKey] / maxVal * 100).toFixed(1)}%`, backgroundColor: COLORS[i] ?? '#c7c9fb' }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────
function Empty({ message = 'Sin datos para el período seleccionado' }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-slate-300 dark:text-slate-700">
      <Package size={36} strokeWidth={1.5} />
      <p className="text-sm mt-3 text-slate-400">{message}</p>
    </div>
  );
}

// ── Tab: Ventas ───────────────────────────────────────────────────────────
function TabVentas({ data }) {
  const { resumen, por_dia, por_forma_pago, top_productos, top_clientes } = data;
  const sinDatos = !por_dia?.length;

  return (
    <div className="space-y-5">
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Total facturado"    value={moneda(resumen.total)}          color="primary" />
        <KpiCard label="Facturas emitidas"  value={Math.round(resumen.cantidad)}   sub={`Ticket promedio ${moneda(resumen.ticket_promedio)}`} color="blue" />
        <KpiCard label="Descuentos"         value={moneda(resumen.descuentos)}     color="amber" />
        <KpiCard label="Neto (sin desc.)"   value={moneda(resumen.total - resumen.descuentos)} color="green" />
      </div>

      {/* Gráfico por día */}
      <div className="card dark:bg-slate-800">
        <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-4">Ventas por día</h3>
        {sinDatos ? <Empty /> : (
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={por_dia} margin={{ left: -10, right: 4 }}>
              <defs>
                <linearGradient id="gVentas" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"   stopColor="#7175ef" stopOpacity={0.18} />
                  <stop offset="100%" stopColor="#7175ef" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
              <XAxis dataKey="dia" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false}
                tickFormatter={v => v.slice(5)} />
              <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false}
                width={65} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
              <Tooltip content={<ChartTip />} cursor={{ stroke: '#7175ef', strokeWidth: 1.5, strokeDasharray: '4 4' }} />
              <Area type="monotone" dataKey="total" stroke="#7175ef" strokeWidth={2.5}
                fill="url(#gVentas)" dot={false} activeDot={{ r: 5, fill: '#7175ef', stroke: '#fff', strokeWidth: 2 }} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Top productos + forma de pago */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="card dark:bg-slate-800">
          <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-4">Top productos vendidos</h3>
          {top_productos?.length ? <TopList items={top_productos} labelKey="nombre" valueKey="total" /> : <Empty />}
        </div>
        <div className="card dark:bg-slate-800">
          <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-4">Por forma de pago</h3>
          {por_forma_pago?.length ? <TopList items={por_forma_pago} labelKey="forma_pago" valueKey="total" /> : <Empty />}
        </div>
      </div>

      {/* Top clientes */}
      {top_clientes?.some(c => c.cliente !== 'Sin cliente') && (
        <div className="card dark:bg-slate-800">
          <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-4">Top clientes</h3>
          <TopList items={top_clientes.filter(c => c.cliente !== 'Sin cliente')} labelKey="cliente" valueKey="total" />
        </div>
      )}
    </div>
  );
}

// ── Tab: Compras ──────────────────────────────────────────────────────────
function TabCompras({ data }) {
  const { resumen, por_dia, top_proveedores, top_productos } = data;
  const sinDatos = !por_dia?.length;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <KpiCard label="Total comprado"      value={moneda(resumen.total)}        color="primary" />
        <KpiCard label="Facturas de compra"  value={Math.round(resumen.cantidad)} sub={`Promedio ${moneda(resumen.promedio)}`} color="blue" />
        <KpiCard label="Promedio por factura" value={moneda(resumen.promedio)}    color="amber" />
      </div>

      <div className="card dark:bg-slate-800">
        <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-4">Compras por día</h3>
        {sinDatos ? <Empty /> : (
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={por_dia} margin={{ left: -10, right: 4 }}>
              <defs>
                <linearGradient id="gCompras" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"   stopColor="#38bdf8" stopOpacity={0.18} />
                  <stop offset="100%" stopColor="#38bdf8" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
              <XAxis dataKey="dia" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false}
                tickFormatter={v => v.slice(5)} />
              <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false}
                width={65} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
              <Tooltip content={<ChartTip />} cursor={{ stroke: '#38bdf8', strokeWidth: 1.5, strokeDasharray: '4 4' }} />
              <Area type="monotone" dataKey="total" stroke="#38bdf8" strokeWidth={2.5}
                fill="url(#gCompras)" dot={false} activeDot={{ r: 5, fill: '#38bdf8', stroke: '#fff', strokeWidth: 2 }} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="card dark:bg-slate-800">
          <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-4">Top proveedores</h3>
          {top_proveedores?.length ? <TopList items={top_proveedores} labelKey="proveedor" valueKey="total" /> : <Empty />}
        </div>
        <div className="card dark:bg-slate-800">
          <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-4">Top productos comprados</h3>
          {top_productos?.length ? <TopList items={top_productos} labelKey="nombre" valueKey="total" /> : <Empty />}
        </div>
      </div>
    </div>
  );
}

// ── Tab: Stock ────────────────────────────────────────────────────────────
function TabStock({ data }) {
  const { resumen, productos } = data;
  const [search, setSearch] = useState('');

  const filtrados = (productos ?? []).filter(p =>
    !search || p.nombre.toLowerCase().includes(search.toLowerCase()) ||
    p.categoria.toLowerCase().includes(search.toLowerCase())
  );

  const sinStockCount = (productos ?? []).filter(p => p.controla_stock && p.stock_actual <= 0).length;
  const bajoStockCount = (productos ?? []).filter(p => p.controla_stock && p.stock_minimo > 0 && p.stock_actual <= p.stock_minimo && p.stock_actual > 0).length;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Total productos"    value={Math.round(resumen.total_productos)} color="primary" />
        <KpiCard label="Valor a costo"      value={moneda(resumen.valor_costo)}         color="blue" />
        <KpiCard label="Valor a precio venta" value={moneda(resumen.valor_venta)}       color="green" />
        <KpiCard label="Con stock crítico"  value={Math.round(resumen.bajo_stock)}
          sub={resumen.bajo_stock > 0 ? `${sinStockCount} sin stock, ${bajoStockCount} bajo mínimo` : 'Sin alertas'}
          color={resumen.bajo_stock > 0 ? 'red' : 'green'} />
      </div>

      {/* Buscador */}
      <div className="relative max-w-xs">
        <Package size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          className="input pl-8"
          placeholder="Buscar producto o categoría..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Tabla */}
      <div className="card !p-0 overflow-hidden dark:bg-slate-800">
        <div className="table-wrapper">
          <table className="table">
            <thead>
              <tr>
                <th>Producto</th>
                <th>Categoría</th>
                <th className="text-right">Stock</th>
                <th className="text-right">Mínimo</th>
                <th className="text-right">Costo unit.</th>
                <th className="text-right">Precio venta</th>
                <th className="text-right">Valor stock</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {filtrados.map((p, i) => {
                const sinStock  = p.controla_stock && p.stock_actual <= 0;
                const bajo      = p.controla_stock && p.stock_minimo > 0 && p.stock_actual <= p.stock_minimo && p.stock_actual > 0;
                return (
                  <tr key={i}>
                    <td className="font-medium text-slate-900 dark:text-white">{p.nombre}</td>
                    <td className="text-slate-400 text-sm">{p.categoria}</td>
                    <td className={`text-right tabular-nums font-semibold ${sinStock ? 'text-red-600 dark:text-red-400' : bajo ? 'text-orange-500' : 'text-slate-900 dark:text-white'}`}>
                      {cantidad(p.stock_actual, p.unidad)}
                    </td>
                    <td className="text-right text-slate-400 tabular-nums text-sm">
                      {p.controla_stock && p.stock_minimo > 0 ? cantidad(p.stock_minimo, p.unidad) : '—'}
                    </td>
                    <td className="text-right text-slate-500 tabular-nums text-sm">{moneda(p.precio_costo)}</td>
                    <td className="text-right text-slate-500 tabular-nums text-sm">{moneda(p.precio_venta)}</td>
                    <td className="text-right font-semibold tabular-nums text-slate-900 dark:text-white">{moneda(p.valor_costo)}</td>
                    <td>
                      {sinStock ? (
                        <span className="badge-low">Sin stock</span>
                      ) : bajo ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-400 ring-1 ring-orange-200/70">Bajo mín.</span>
                      ) : (
                        <span className="badge-ok">OK</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Tab: Rentabilidad ─────────────────────────────────────────────────────
function TabRentabilidad({ data }) {
  const { resumen, productos } = data;
  const sinDatos = !productos?.length;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Ingresos"       value={moneda(resumen.ingresos)}  color="primary" />
        <KpiCard label="Costos"         value={moneda(resumen.costos)}    color="amber" />
        <KpiCard label="Ganancia bruta" value={moneda(resumen.ganancia)}  color="green" />
        <KpiCard label="Margen %"       value={`${resumen.margen_pct?.toFixed(1)}%`}
          sub={resumen.ganancia > 0 ? 'Margen positivo' : 'Sin rentabilidad'}
          color={resumen.ganancia > 0 ? 'green' : 'red'} />
      </div>

      {sinDatos ? (
        <div className="card dark:bg-slate-800"><Empty /></div>
      ) : (
        <>
          {/* Gráfico ganancia por producto */}
          <div className="card dark:bg-slate-800">
            <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-4">Ganancia por producto</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={productos.slice(0, 10).slice().reverse()} layout="vertical" barSize={14} margin={{ left: 0, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E2E8F0" />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false}
                  tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                <YAxis dataKey="nombre" type="category" tick={{ fontSize: 11, fill: '#64748B' }}
                  axisLine={false} tickLine={false} width={130}
                  tickFormatter={v => v.length > 18 ? v.slice(0, 16) + '…' : v} />
                <Tooltip content={<ChartTip />} cursor={{ fill: '#f0f1fe', radius: 4 }} />
                <Bar dataKey="ganancia" fill="#10b981" radius={[0, 5, 5, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Tabla detalle */}
          <div className="card !p-0 overflow-hidden dark:bg-slate-800">
            <div className="table-wrapper">
              <table className="table">
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th className="text-right">Vendido</th>
                    <th className="text-right">Ingresos</th>
                    <th className="text-right">Costos</th>
                    <th className="text-right">Ganancia</th>
                    <th className="text-right">Margen</th>
                  </tr>
                </thead>
                <tbody>
                  {productos.map((p, i) => (
                    <tr key={i}>
                      <td className="font-medium text-slate-900 dark:text-white">{p.nombre}</td>
                      <td className="text-right text-slate-400 tabular-nums text-sm">{cantidad(p.unidades_vendidas)}</td>
                      <td className="text-right tabular-nums text-sm text-slate-600 dark:text-slate-300">{moneda(p.ingresos)}</td>
                      <td className="text-right tabular-nums text-sm text-slate-500">{moneda(p.costos)}</td>
                      <td className={`text-right tabular-nums font-semibold ${p.ganancia >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                        {p.ganancia >= 0
                          ? <span className="inline-flex items-center gap-0.5"><ArrowUpRight size={12} />{moneda(p.ganancia)}</span>
                          : <span className="inline-flex items-center gap-0.5"><ArrowDownRight size={12} />{moneda(Math.abs(p.ganancia))}</span>
                        }
                      </td>
                      <td className="text-right">
                        <span className={`text-[12px] font-bold tabular-nums ${p.margen_pct >= 20 ? 'text-emerald-600 dark:text-emerald-400' : p.margen_pct >= 0 ? 'text-amber-600 dark:text-amber-400' : 'text-rose-600'}`}>
                          {p.margen_pct?.toFixed(1)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────────────────
export default function Reportes() {
  const [tab,      setTab]      = useState('ventas');
  const [preset,   setPreset]   = useState('Este mes');
  const [desde,    setDesde]    = useState(PRESETS.find(p => p.label === 'Este mes').get().desde);
  const [hasta,    setHasta]    = useState(PRESETS.find(p => p.label === 'Este mes').get().hasta);
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(false);

  const aplicarPreset = (p) => {
    setPreset(p.label);
    const r = p.get();
    setDesde(r.desde);
    setHasta(r.hasta);
  };

  const fetchReporte = useCallback(async () => {
    setLoading(true);
    setData(null);
    try {
      const params = { tipo: tab };
      if (tab !== 'stock') { params.fecha_desde = desde; params.fecha_hasta = hasta; }
      const res = await api.get('/reportes/index.php', { params });
      setData(res.data);
    } catch {
      toast.error('Error al cargar el reporte');
    } finally { setLoading(false); }
  }, [tab, desde, hasta]);

  useEffect(() => { fetchReporte(); }, [fetchReporte]);

  const fechaLabel =
    tab === 'stock'
      ? 'Estado actual del inventario'
      : desde === hasta
        ? new Date(desde + 'T00:00:00').toLocaleDateString('es-AR', { day: '2-digit', month: 'long', year: 'numeric' })
        : `${new Date(desde + 'T00:00:00').toLocaleDateString('es-AR', { day: '2-digit', month: 'short' })} → ${new Date(hasta + 'T00:00:00').toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' })}`;

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Reportes</h1>
          <p className="text-xs text-slate-400 mt-0.5">{fechaLabel}</p>
        </div>
        <div className="flex items-center gap-2">
          {data && (
            <button
              onClick={() => {
                try {
                  if (tab === 'ventas')       exportarVentas(data, desde, hasta);
                  else if (tab === 'compras') exportarCompras(data, desde, hasta);
                  else if (tab === 'stock')   exportarStock(data);
                  else                        exportarRentabilidad(data, desde, hasta);
                } catch { toast.error('Error al generar el archivo Excel'); }
              }}
              className="btn-secondary text-sm"
            >
              <Download size={14} />
              Exportar XLSX
            </button>
          )}
          <button onClick={fetchReporte} disabled={loading} className="btn-ghost text-sm disabled:opacity-40">
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
            Actualizar
          </button>
        </div>
      </div>

      {/* Filtros de fecha (ocultos para stock) */}
      {tab !== 'stock' && (
        <div className="card dark:bg-slate-800 flex flex-wrap items-end gap-3">
          {/* Presets */}
          <div className="flex flex-wrap gap-1.5">
            {PRESETS.map(p => (
              <button
                key={p.label}
                onClick={() => aplicarPreset(p)}
                className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold transition-colors ${
                  preset === p.label
                    ? 'bg-primary-500 text-white'
                    : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="h-6 w-px bg-slate-200 dark:bg-slate-700 hidden sm:block" />

          {/* Rango personalizado */}
          <div className="flex items-center gap-2">
            <input
              type="date"
              className="input py-1.5 text-sm w-36"
              value={desde}
              onChange={(e) => { setDesde(e.target.value); setPreset(''); }}
            />
            <ChevronRight size={14} className="text-slate-300 shrink-0" />
            <input
              type="date"
              className="input py-1.5 text-sm w-36"
              value={hasta}
              onChange={(e) => { setHasta(e.target.value); setPreset(''); }}
            />
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 dark:bg-slate-800 p-1 rounded-xl w-fit">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-semibold transition-all ${
              tab === t.id
                ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-xs'
                : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300'
            }`}
          >
            <t.icon size={14} />
            {t.label}
          </button>
        ))}
      </div>

      {/* Contenido */}
      {loading ? (
        <div className="flex items-center justify-center h-64 text-slate-300 dark:text-slate-700">
          <RefreshCw size={24} className="animate-spin" />
        </div>
      ) : data ? (
        <>
          {tab === 'ventas'       && <TabVentas       data={data} />}
          {tab === 'compras'      && <TabCompras      data={data} />}
          {tab === 'stock'        && <TabStock        data={data} />}
          {tab === 'rentabilidad' && <TabRentabilidad data={data} />}
        </>
      ) : null}
    </div>
  );
}
